/**
 * AutoSldPanel — v2.43 — Auto-generate a populated SLD from project data.
 *
 * Reads the project's equipment_list (via GET /sld/summary), shows a
 * tier-by-tier breakdown so the user knows what they're going to get,
 * and triggers POST /sld/generate to download the DXF. v2.43 adds a
 * symbol-pack selector (IEEE 315 vs IEC 60617).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Lightning, DownloadSimple, Spinner, Stack,
} from "@phosphor-icons/react";

async function postBlob(path) {
  const r = await api.post(path, null, { responseType: "blob" });
  return r.data;
}

export default function AutoSldPanel({ project }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(
    project?.sld_symbol_pack || "ieee315",
  );

  // v2.43 — fetch the available packs once on mount (cheap public endpoint).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/templates/symbols/packs");
        if (!cancelled) setPacks(r.data?.items || []);
      } catch (e) {
        // Non-blocking — selector falls back to the two hard-coded options.
        if (!cancelled) setPacks([
          { id: "ieee315",  label: "IEEE Std 315 / ANSI Y32.2", region: "North America" },
          { id: "iec60617", label: "IEC 60617 / EN 60617",      region: "Europe / International" },
        ]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-fetch the tier summary every time the user flips packs (the
  // symbols_used list + per-tier counts can change because the prefix
  // differs — the backend recomputes when `?pack=` is supplied).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/projects/${project.id}/sld/summary?pack=${selectedPack}`,
        );
        if (!cancelled) setSummary(r.data);
      } catch (e) {
        if (!cancelled) toast.error(`Couldn't load SLD summary: ${e?.message || e}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id, selectedPack]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const blob = await postBlob(
        `/projects/${project.id}/sld/generate?pack=${selectedPack}`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (project.name || "project").replace(/[^a-z0-9]+/gi, "_");
      a.download = `${safe}_auto_sld_${selectedPack}.dxf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Auto-SLD (${selectedPack.toUpperCase()}) downloaded — open it in your CAD app`);
    } catch (e) {
      toast.error(`Generation failed: ${e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="border border-[#FF9F0A]/30 bg-[#1A140A] p-4 mb-6"
          data-testid="auto-sld-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 p-2 bg-[#FF9F0A]/15 border border-[#FF9F0A]/30">
            <Lightning size={20} weight="fill" className="text-[#FF9F0A]" />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-white flex items-center gap-2">
              Auto-SLD
              <span className="px-1.5 py-0.5 bg-[#FF9F0A] text-black text-[9px] font-bold uppercase tracking-wider">
                NEW
              </span>
            </div>
            <p className="text-[12px] text-gray-400 mt-1 leading-snug">
              Generates a complete Single-Line Diagram from this project&apos;s equipment list — classifies every item, wires fed-from links, drops in standards-compliant symbols, and exports a CAD-ready DXF.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {/* v2.43 — Symbol-pack selector */}
          <div className="flex items-center gap-2">
            <label htmlFor="auto-sld-pack"
                   className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
              Pack
            </label>
            <select id="auto-sld-pack"
                    value={selectedPack}
                    onChange={(e) => setSelectedPack(e.target.value)}
                    disabled={generating}
                    className="bg-[#0F1218] border border-white/15 text-white text-[12px] font-mono px-2 py-1 focus:border-[#FF9F0A] focus:outline-none disabled:opacity-50"
                    data-testid="auto-sld-pack-select">
              {(packs.length ? packs : [
                { id: "ieee315",  label: "IEEE 315"  },
                { id: "iec60617", label: "IEC 60617" },
              ]).map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </div>
          <button onClick={onGenerate}
                  disabled={generating || loading || !summary?.total_items}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="auto-sld-generate-btn">
            {generating ? <Spinner className="animate-spin" size={14} />
                        : <DownloadSimple size={14} />}
            {generating ? "Generating…" : "Generate Auto-SLD (.dxf)"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 text-[12px] font-mono text-gray-500">Loading summary…</div>
      ) : !summary?.total_items ? (
        <div className="mt-4 text-[12px] font-mono text-gray-500 border-l-2 border-gray-700 pl-3"
              data-testid="auto-sld-empty">
          No equipment in this project yet — add equipment in the Builder before generating.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"
              data-testid="auto-sld-tier-grid">
          {summary.tiers.map((t) => (
            <div key={t.tier}
                  className="border border-white/10 bg-[#0F1218] p-2"
                  data-testid={`auto-sld-tier-${t.tier}`}>
              <div className="flex items-center gap-1.5">
                <Stack size={11} className="text-[#FF9F0A]" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
                  Tier {t.tier}
                </span>
              </div>
              <div className="text-xs font-mono text-gray-300 mt-0.5 truncate"
                    title={t.label}>
                {t.label}
              </div>
              <div className="text-lg font-bold text-[#FF9F0A] font-mono mt-1">
                {t.count}
              </div>
            </div>
          ))}
          <div className="border border-[#FF9F0A]/30 bg-[#FF9F0A]/5 p-2"
                data-testid="auto-sld-symbol-pack">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#FF9F0A]">
              Symbol pack
            </div>
            <div className="text-xs font-mono text-[#FF9F0A] mt-0.5">
              {summary.symbol_pack}
            </div>
            <div className="text-[10px] font-mono text-gray-500 mt-1">
              {summary.total_items} items mapped
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
